/**
 * Swatch picker and swatch request form.
 *
 * The maximum number of swatches is the number of slots rendered in the tray
 * (Aurelo hardcoded 6 in the script while rendering the slots from a Liquid
 * range, so the two could drift apart).
 *
 * Requires request-forms.js, loaded immediately before this file.
 */
(() => {
  const { onReady, klaviyoTrack, postForm, setVisible } = window.aureloRequestForms;

  const ZAPIER_HOOK = "https://hooks.zapier.com/hooks/catch/9220888/4mjciqc/silent/";

  const PRODUCT_TYPE = "snuglife";

  const selectors = {
    picker: ".swatches-render-wrapper",
    swatch: ".single-swatch",
    tray: ".selected-swatches-wrapper",
    slot: ".selected-swatch",
    error: ".errorBlock",
    form: "form#requestSwatches",
  };

  const picker = () => document.querySelector(selectors.picker);
  const slots = () => document.querySelectorAll(`${selectors.tray} ${selectors.slot}`);
  const filledSlots = () => document.querySelectorAll(`${selectors.tray} .filledSwatch`);
  const slotByName = (name) => document.querySelector(`${selectors.tray} ${selectors.slot}[data-swatch-name="${name}"]`);

  const pickerSwatchesByName = (name) => {
    const wrapper = picker();
    return wrapper ? wrapper.querySelectorAll(`${selectors.swatch}[data-swatch-name="${name}"]`) : [];
  };

  /**
   * Shows/hides the form and swaps the Next/Cancel buttons. Called from the
   * inline onclick on those buttons, so it has to be global.
   */
  window.ShowSwatchForm = (show = true) => {
    const formContainer = document.getElementById("select-swatches-form-container");
    const swatchSelectContainer = document.getElementById("select-swatches-container");
    const nextButton = document.getElementById("swatchNext");
    const cancelButton = document.getElementById("swatchCancel");
    const tray = document.querySelector(".my-swatches-container");

    if (show && filledSlots().length === 0) {
      // Nothing picked yet — flash the tray rather than showing an empty form.
      tray?.classList.add("show-message");
      setTimeout(() => tray?.classList.remove("show-message"), 3000);
      return;
    }

    formContainer.classList.toggle("hidden", !show);
    swatchSelectContainer.classList.toggle("hidden", show);
    nextButton.classList.toggle("hidden", show);
    cancelButton.classList.toggle("hidden", !show);
  };

  /**
   * The swatch name is "<title> <title 2>" and is NOT trimmed: the picker
   * renders it into data-swatch-name straight from Liquid, so a swatch with no
   * second title carries a trailing space. Trimming here would stop the
   * attribute selectors in pickerSwatchesByName/slotByName from matching.
   */
  const nameFor = (swatch) => {
    if (swatch.dataset.swatchName !== undefined) return swatch.dataset.swatchName;
    const img = swatch.querySelector("img");
    return img ? `${img.dataset.name1 || ""} ${img.dataset.name2 || ""}` : "";
  };

  const addNameLabels = (slot, img) => {
    [img.dataset.name1, img.dataset.name2].forEach((text) => {
      if (!text) return;
      const label = document.createElement("div");
      label.className = "name text-center font-bold text-xs";
      label.textContent = text;
      slot.appendChild(label);
    });
  };

  /** Copies a picker swatch into the first empty slot in the tray. */
  const fillFirstEmptySlot = (swatch) => {
    const sourceImage = swatch.querySelector("img");
    if (!sourceImage) return;

    const slot = Array.from(slots()).find((candidate) => !candidate.querySelector(`${selectors.swatch}`).hasChildNodes());
    if (!slot) return;

    const name = nameFor(swatch);
    const image = document.createElement("img");
    image.className = "h-full! w-full object-fill";
    image.src = sourceImage.src;
    image.id = sourceImage.dataset.id;
    image.alt = name;

    slot.classList.add("filledSwatch");
    slot.dataset.swatchName = name;
    slot.querySelector(selectors.swatch).replaceChildren(image);

    const closeButton = slot.querySelector(".close-icon");
    if (closeButton) {
      closeButton.dataset.swatchName = name;
      closeButton.classList.remove("hidden");
    }

    addNameLabels(slot, sourceImage);
  };

  /** Empties the tray slot holding the named swatch. */
  const clearSlot = (name) => {
    const slot = slotByName(name);
    if (!slot) return;

    slot.classList.remove("filledSwatch");
    delete slot.dataset.swatchName;
    slot.querySelector(selectors.swatch).replaceChildren();
    slot.querySelectorAll(".name").forEach((label) => label.remove());

    const closeButton = slot.querySelector(".close-icon");
    if (closeButton) {
      delete closeButton.dataset.swatchName;
      closeButton.classList.add("hidden");
    }
  };

  /**
   * Deselects a swatch everywhere it appears. A swatch can be listed under more
   * than one row, so all of its copies have to stay in sync.
   */
  const deselect = (name) => {
    setVisible(document.querySelector(selectors.error), false);
    pickerSwatchesByName(name).forEach((swatch) => swatch.classList.remove("active"));
    clearSlot(name);
  };

  const select = (swatch) => {
    const errorBlock = document.querySelector(selectors.error);

    if (filledSlots().length >= slots().length) {
      setVisible(errorBlock, true);
      if (window.innerWidth <= 768) {
        errorBlock?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    pickerSwatchesByName(nameFor(swatch)).forEach((copy) => copy.classList.add("active"));
    fillFirstEmptySlot(swatch);
  };

  /**
   * Removes a swatch from the tray. Called from the inline onclick on each
   * slot's close button, so it has to be global.
   */
  window.removeSwatchFromForm = (element) => {
    const name = element.dataset.swatchName || element.closest(selectors.slot)?.dataset.swatchName;
    if (name) deselect(name);
  };

  const initPicker = () => {
    const wrapper = picker();
    if (!wrapper) return;

    // Anything rendered already selected populates the tray on load.
    const seen = new Set();
    wrapper.querySelectorAll(`${selectors.swatch}.active`).forEach((swatch) => {
      const name = nameFor(swatch);
      if (seen.has(name)) return;
      seen.add(name);
      fillFirstEmptySlot(swatch);
    });

    wrapper.querySelectorAll(selectors.swatch).forEach((swatch) => {
      swatch.addEventListener("click", () => {
        if (swatch.classList.contains("active")) {
          deselect(nameFor(swatch));
        } else {
          select(swatch);
        }
      });
    });
  };

  const submitSwatchRequest = (event) => {
    const form = event.currentTarget;
    const successPanel = document.querySelector(".swatches-success-message");
    const formContainer = document.getElementById("select-swatches-form-container");

    const swatches = {};
    filledSlots().forEach((slot, index) => {
      swatches[index] = slot.dataset.swatchName;
    });

    let whereDidYouHear = form.querySelector('[name="where_did_you_hear_about_us"]').value;
    if (whereDidYouHear === "Other") {
      whereDidYouHear = form.querySelector('[name="catalogue_Where_did_you_hear_about_us_other"]').value;
    }

    const heardOfUs = form.querySelector('input[name="have_you_heard_of_bridgman_before"]:checked');
    const acceptsMarketing = form.querySelector('input[name="data-capture"]').checked;
    const email = document.getElementById("email").value;

    const swatchParams = {
      producttype: PRODUCT_TYPE,
      $first_name: document.getElementById("first_name").value,
      $last_name: document.getElementById("last_name").value,
      $Zip: document.getElementById("post_code").value,
      $email: email,
      $address1: document.getElementById("address_line_1").value,
      $address2: document.getElementById("address_line_2").value,
      $city: document.getElementById("city").value,
      $country: document.getElementById("country").value,
      $phone_number: document.getElementById("phone").value,
      $source: whereDidYouHear,
      swatches,
      $have_you_heard_of_bridgman_before: heardOfUs ? heardOfUs.value : "No",
      $accepts_marketing: acceptsMarketing,
    };

    const properties = {
      producttype: PRODUCT_TYPE,
      $email: email,
      $source: swatchParams.$source,
      $accepts_marketing: acceptsMarketing,
    };

    klaviyoTrack(properties, { event: "Request Swatches", trackData: swatchParams }, () => {
      postForm(ZAPIER_HOOK, swatchParams)
        .then(() => {
          setTimeout(() => {
            formContainer.classList.add("hidden");
            successPanel.classList.remove("hidden");
          }, 1000);
        })
        .catch((error) => {
          console.error("Swatch request failed", error);
        });
    });
  };

  onReady(() => {
    initPicker();

    const form = document.querySelector(selectors.form);
    if (!form) return;

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      submitSwatchRequest(event);
    });
  });
})();
